import { useState } from 'react'

import {
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	flip,
	offset,
	shift,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from '@floating-ui/react'
import { IconCreditCard, IconLogout, IconUserCircle } from '@tabler/icons-react'

import { FormattedMessage, useNavigation } from '@subaio/components'
import { type Api, logger } from '@subaio/utils'

import { Menu } from './Menu'
import { MenuItem } from './MenuItem'
import { Trigger } from './Trigger'

type Props = {
	api: Api
	isB2C: boolean
	onLogout: () => void
}

export const UserMenu = ({ api, isB2C, onLogout }: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const { onUpdateUrlTo } = useNavigation()
	const { context, floatingStyles, refs } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom-end',
		whileElementsMounted: autoUpdate,
		middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
	})
	const { getFloatingProps, getReferenceProps } = useInteractions([
		useDismiss(context),
		useRole(context, { role: 'menu' }),
	])

	return (
		<>
			<Trigger
				ref={refs.setReference}
				type='button'
				{...getReferenceProps({
					'aria-label': 'Open user menu',
					onClick: () => setIsOpen((isOpen) => !isOpen),
				})}
			>
				<IconUserCircle size={42} strokeWidth={1.2} />
			</Trigger>
			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<Menu ref={refs.setFloating} style={floatingStyles} aria-label='User menu' {...getFloatingProps()}>
							{!isB2C && (
								<MenuItem
									role='menuitem'
									onClick={async () => {
										try {
											const { billingUrl } = await api.get<{ billingUrl: string }>('/billing/status')
											window.location.assign(billingUrl)
										} catch (error) {
											logger.error(error)
										}
									}}
								>
									<IconCreditCard aria-hidden size={18} />
									<span>
										<FormattedMessage id='header.billing' defaultMessage='Billing' />
									</span>
								</MenuItem>
							)}
							<MenuItem
								role='menuitem'
								onClick={() => {
									onLogout()
									onUpdateUrlTo({ name: 'Login' })
								}}
							>
								<IconLogout aria-hidden size={18} />
								<span>
									<FormattedMessage id='header.logout' defaultMessage='Log out' />
								</span>
							</MenuItem>
						</Menu>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</>
	)
}
